import type { Path, UseFormReturn } from 'react-hook-form';
import {
  Badge,
  Disclosure,
  DisclosureButton,
  Labeled,
  Stack,
  Switch,
} from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IGeneralSettingsFormValues } from './GeneralSettingsForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';

export type IGeneralSettingsFormGuestArticlesSectionProps = IContainerProps & {
  form: UseFormReturn<IGeneralSettingsFormValues>;
  submittingFields?: Array<Path<IGeneralSettingsFormValues>>;
  onChange?: (
    form: UseFormReturn<IGeneralSettingsFormValues>,
    updatedFields?: Array<Path<IGeneralSettingsFormValues>>,
  ) => void;
  proFeature?: boolean;
};

export const GeneralSettingsFormGuestArticlesSection: React.FC<
  IGeneralSettingsFormGuestArticlesSectionProps
> = (props) => {
  const { sx, form, submittingFields, onChange, proFeature } = props;
  const { t } = useTranslation();

  const usersEnabled = form.watch('usersEnabled');

  return (
    <Disclosure
      sx={sx}
      initiallyExpanded={
        form.watch('communityArticleCommentsEnabled') ||
        form.watch('communityArticleShareEnabled')
      }
      trigger={
        <DisclosureButton>
          {proFeature ? (
            <Stack horizontal gap={2}>
              {t('GeneralSettingsFormGuestArticlesSection.title')}
              <Badge value="Pro" />
            </Stack>
          ) : (
            t('GeneralSettingsFormGuestArticlesSection.title')
          )}
        </DisclosureButton>
      }
      disabled={proFeature}
    >
      <FormSection sub>
        <Labeled
          label={t(
            'GeneralSettingsFormGuestArticlesSection.field.communityArticleCommentsEnabled.label',
          )}
          supportingText={t(
            'GeneralSettingsFormGuestArticlesSection.field.communityArticleCommentsEnabled.supportingText',
          )}
          hasError={!usersEnabled}
          errorText={
            !usersEnabled
              ? t('GeneralSettingsFormGuestArticlesSection.error.usersRequired')
              : undefined
          }
          labelPosition="right"
          disabled={proFeature}
        >
          <FormField
            name="communityArticleCommentsEnabled"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () =>
                onChange?.(form, ['communityArticleCommentsEnabled']),
            }}
            loading={submittingFields?.includes(
              'communityArticleCommentsEnabled',
            )}
          />
        </Labeled>
        <Labeled
          label={t(
            'GeneralSettingsFormGuestArticlesSection.field.communityArticleShareEnabled.label',
          )}
          labelPosition="right"
          disabled={proFeature}
        >
          <FormField
            name="communityArticleShareEnabled"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () =>
                onChange?.(form, ['communityArticleShareEnabled']),
            }}
            loading={submittingFields?.includes('communityArticleShareEnabled')}
          />
        </Labeled>
      </FormSection>
    </Disclosure>
  );
};
